import { HStack, Stack, VStack } from '@chakra-ui/react'
import { GrandText, ImageBox } from 'ui'
import { type ICartItemGroupDetail } from 'ecosystem'
import { getProductImages } from 'shared-utils/utils/products'
import { useMemo } from 'react'
import ProductPriceComponent from '../../../../../products/components/ProductPrice'
import CheckoutCartSubItems from '../CheckoutCartSubItems'
import { CheckoutCartServices } from '../CheckoutCartServices'
import { useCheckoutContext } from '../../../../hooks'

interface CheckoutCartItemGroupDetailProps {
  item: ICartItemGroupDetail
  onServicesIdsUpdate: (productId: string, servicesIds: string[]) => void
}

export const CheckoutCartItemGroupDetail = ({
  item,
  onServicesIdsUpdate
}: CheckoutCartItemGroupDetailProps) => {
  const { product, subItems, originalQty = 1 } = item
  const { services: allAvailableServices } = useCheckoutContext()
  const { mainImageUrl } = getProductImages(product.medias)

  const services = useMemo(
    () => allAvailableServices?.filter((service) => !!product.serviceIds?.includes(service.id)),
    [allAvailableServices, product.serviceIds]
  )

  return (
    <VStack borderColor="gray.100" borderWidth={1} p={4} spacing={4} w="100%">
      <Stack direction={{ base: 'column', sm: 'row' }} w="100%">
        <GrandText
          flex={1}
          fontSize={{
            base: 'xs',
            md: 'sm'
          }}
          isTruncated
          w={{
            base: '100%',
            sm: 'auto'
          }}
        >
          {product.name}
        </GrandText>

        <HStack spacing={4} w="full">
          <ImageBox
            alignSelf="flex-start"
            imageProps={{
              alt: product.name,
              title: product.name,
              src: mainImageUrl,
              style: {
                objectFit: 'contain'
              },
              sizes: '7em'
            }}
            w={{ base: 16, md: 20 }}
            h={{ base: 16, md: 20 }}
          />
          <VStack flex={1} w="full">
            <HStack
              justifyContent={{
                base: 'flex-end',
                sm: 'initial'
              }}
              w="100%"
            >
              <GrandText display={{ base: 'none', sm: 'initial' }} flexGrow={1} w="auto">
                {product.name}
              </GrandText>

              <HStack alignItems="baseline">
                <ProductPriceComponent
                  price={product.price}
                  originalPrice={product.originalPrice}
                  priceWithoutVat={product.priceWithoutVat}
                  originalPriceWithoutVat={product.originalPriceWithoutVat}
                  priceProps={{
                    color: 'inherit'
                  }}
                  alignItems="baseline"
                  whiteSpace="nowrap"
                />

                <GrandText fontSize="xs" w="auto">
                  {originalQty}x
                </GrandText>
              </HStack>
            </HStack>
          </VStack>
        </HStack>
      </Stack>

      {!!subItems?.length && <CheckoutCartSubItems subItems={subItems} />}

      {services?.length && (
        <CheckoutCartServices
          services={services}
          onChange={(servicesIds) => {
            onServicesIdsUpdate(product.id, servicesIds)
          }}
        />
      )}
    </VStack>
  )
}
